import { useEffect, useState } from 'react';

/**
 * The signed-in account's username.
 *
 * Read from `localStorage`, which is where `services/api.ts` writes it on login and
 * where `AuthContext` and every other caller reads it. NOT from `useUserStore`: that
 * store declares a `username` and a `setUsername`, but nothing in the app ever calls
 * the setter, so it is always `''` — which silently made every account look
 * unrecognised to the model pickers.
 *
 * Kept live for the tab: `storage` fires when another tab signs in or out, and the
 * custom `patentloop:username` event covers this tab, where `storage` does not fire.
 */
export function useCurrentUsername(): string {
  const [username, setUsername] = useState(() => readUsername());

  useEffect(() => {
    const sync = () => setUsername(readUsername());
    window.addEventListener('storage', sync);
    window.addEventListener('patentloop:username', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('patentloop:username', sync);
    };
  }, []);

  return username;
}

export function readUsername(): string {
  try {
    return localStorage.getItem('username') || '';
  } catch {
    // Private browsing, or storage disabled.
    return '';
  }
}
